import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'rsuite';
import { commanderTax, MOVE_TARGET_LABELS, ZONE_LABELS } from '../../../shared/playtest';
import CardDetailPanel from './CardDetailPanel';
import CardTile from './CardTile';
import { displayOf, refusalText } from './playtestDisplay';
import { PlaytestDropZone, useDragSourceProps } from './PlaytestDnD';
import PlaytestLibraryPanel from './PlaytestLibraryPanel';
import { usePlaytestSessions } from './PlaytestSessionProvider';

const SPACING = { section: 12, row: 8 };
const STRIP_MIN_WIDTH = 96;
const LIBRARY_ZONE = 'library';
/** Delay before a dragged dialog hides. The browser snapshots the drag image after dragstart. */
const DRAG_HIDE_DELAY_MS = 0;
/** Hides a dialog during a drag without unmounting it, so its dragged card stays mounted. */
const HIDDEN_DIALOG_STYLE = { visibility: 'hidden', pointerEvents: 'none' };

const styles = {
  row: { display: 'flex', flexWrap: 'wrap', gap: SPACING.section, alignItems: 'stretch' },
  pile: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACING.row },
  strip: { minWidth: STRIP_MIN_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  libraryGroup: { display: 'flex', alignItems: 'center', gap: SPACING.row },
  dialogList: { display: 'flex', flexWrap: 'wrap', gap: SPACING.row, alignItems: 'flex-start' },
  column: { display: 'flex', flexDirection: 'column', gap: SPACING.row, alignItems: 'center' },
  detail: { marginBottom: SPACING.section },
};

/**
 * One zone's contents. Graveyard and exile tiles open detail; command-zone
 * cards show their tax and a Cast action.
 */
const ZoneCards = ({ collectionId, session, zone, onOpenDetail, dragHandlers }) => {
  const { dispatchAction } = usePlaytestSessions();
  const dragSourceProps = useDragSourceProps();
  const state = session.engine;
  const ids = state.zones[zone];
  const [castRefusal, setCastRefusal] = useState(null); // { message, contentsKey }; contentsKey is null until shown
  const cast = (instanceId) => {
    const result = dispatchAction(collectionId, { type: 'castFromCommand', instanceId });
    setCastRefusal(result === 'ok' ? null : { message: refusalText(result), contentsKey: null });
  };
  const contentsKey = ids.join(',');
  // Records the pile the refusal first showed against. A later change to the pile clears it.
  useEffect(() => {
    setCastRefusal((current) => {
      if (!current || current.contentsKey === contentsKey) return current;
      return current.contentsKey === null ? { ...current, contentsKey } : null;
    });
  }, [contentsKey, castRefusal]);

  const refusalShown =
    castRefusal !== null && (castRefusal.contentsKey === null || castRefusal.contentsKey === contentsKey);
  const refusal = refusalShown ? <span data-testid="command-cast-error">{castRefusal.message}</span> : null;

  if (ids.length === 0) {
    return (
      <>
        <span data-testid={`pile-empty-${zone}`}>No cards.</span>
        {refusal}
      </>
    );
  }

  if (zone === 'command') {
    return (
      <div data-testid="command-zone" style={styles.dialogList}>
        {ids.map((instanceId) => {
          const view = displayOf(session, state.instances[instanceId].cardId);
          const tax = commanderTax(state, instanceId);
          return (
            <div key={instanceId} style={styles.column}>
              <CardTile
                testId={`command-card-${instanceId}`}
                name={view.name}
                typeLine={view.typeLine}
                imageSmall={view.imageSmall}
                dragProps={dragSourceProps(instanceId, dragHandlers)}
                onClick={() => onOpenDetail(instanceId)}
              />
              <span data-testid={`command-tax-${instanceId}`}>{`Tax +${tax}`}</span>
              <Button
                size="xs"
                data-testid={`command-cast-${instanceId}`}
                onClick={() => cast(instanceId)}
              >
                {`Cast (Tax +${tax})`}
              </Button>
            </div>
          );
        })}
        {refusal}
      </div>
    );
  }

  return (
    <div data-testid={`pile-cards-${zone}`} style={styles.dialogList}>
      {ids.map((instanceId) => {
        const view = displayOf(session, state.instances[instanceId].cardId);
        return (
          <CardTile
            key={instanceId}
            testId={`${zone}-card-${instanceId}`}
            name={view.name}
            typeLine={view.typeLine}
            imageSmall={view.imageSmall}
            dragProps={dragSourceProps(instanceId, dragHandlers)}
            onClick={() => onOpenDetail(instanceId)}
          />
        );
      })}
    </div>
  );
};

ZoneCards.propTypes = {
  collectionId: PropTypes.string.isRequired,
  session: PropTypes.object.isRequired,
  zone: PropTypes.string.isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  dragHandlers: PropTypes.shape({ onStart: PropTypes.func, onEnd: PropTypes.func }).isRequired,
};

/**
 * Opened from a pile. Holds its own detail selection so the detail panel
 * renders inside the dialog instead of stacking another modal.
 */
const PileDialog = ({ collectionId, session, zone, onClose }) => {
  const [detailId, setDetailId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const closeDetail = useCallback(() => setDetailId(null), []);

  // A dragged dialog card hides the dialog, which stays mounted so the drag source survives.
  // A drag start schedules the hide. Unmounting cancels it, so it never sets state on a dead dialog.
  const dragTimer = useRef(null);
  useEffect(() => () => clearTimeout(dragTimer.current), []);
  // The piles, hand and battlefield become reachable. Dragend closes the dialog. A drop closes it
  // too: a drop that moves the card unmounts the source, and dragend then never reaches React.
  const dragHandlers = {
    onStart: () => {
      dragTimer.current = setTimeout(() => setDragging(true), DRAG_HIDE_DELAY_MS);
    },
    onEnd: () => onClose(),
  };
  const hiddenStyle = dragging ? HIDDEN_DIALOG_STYLE : undefined;

  useEffect(() => {
    if (!dragging) return undefined;
    const closeOnDrop = () => onClose();
    document.addEventListener('drop', closeOnDrop, true);
    return () => document.removeEventListener('drop', closeOnDrop, true);
  }, [dragging, onClose]);

  return (
    <Modal open onClose={onClose} size="md" style={hiddenStyle} backdropStyle={hiddenStyle}>
      <Modal.Header>
        <Modal.Title>{ZONE_LABELS[zone]}</Modal.Title>
      </Modal.Header>
      <Modal.Body data-testid={`pile-dialog-${zone}`}>
        {detailId !== null ? (
          <div style={styles.detail}>
            <CardDetailPanel
              collectionId={collectionId}
              session={session}
              instanceId={detailId}
              onClose={closeDetail}
            />
          </div>
        ) : null}
        {zone === LIBRARY_ZONE ? (
          <PlaytestLibraryPanel
            collectionId={collectionId}
            session={session}
            onOpenDetail={setDetailId}
            dragHandlers={dragHandlers}
          />
        ) : (
          <ZoneCards
            collectionId={collectionId}
            session={session}
            zone={zone}
            onOpenDetail={setDetailId}
            dragHandlers={dragHandlers}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

PileDialog.propTypes = {
  collectionId: PropTypes.string.isRequired,
  session: PropTypes.object.isRequired,
  zone: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Library, graveyard, exile and command piles. Each pile is a drop target and
 * opens its dialog on click. The library has Top and Bottom drop strips.
 */
const PlaytestPiles = ({ collectionId, session, drops }) => {
  const [openZone, setOpenZone] = useState(null);
  const closeDialog = useCallback(() => setOpenZone(null), []);
  const state = session.engine;

  const renderPile = (zone) => (
    <PlaytestDropZone
      key={zone}
      testId={`pile-drop-${zone}`}
      target={{ kind: 'zone', zone }}
      drops={drops}
      style={styles.pile}
    >
      <Button data-testid={`pile-${zone}`} onClick={() => setOpenZone(zone)}>
        {ZONE_LABELS[zone]}
      </Button>
      <span data-testid={`pile-${zone}-count`}>{state.zones[zone].length}</span>
    </PlaytestDropZone>
  );

  const libraryStrip = (to) => (
    <PlaytestDropZone
      testId={`pile-drop-${to}`}
      target={{ kind: 'zone', zone: to }}
      drops={drops}
      style={styles.strip}
    >
      {MOVE_TARGET_LABELS[to]}
    </PlaytestDropZone>
  );

  return (
    <section data-testid="playtest-piles" style={styles.row}>
      <div style={styles.libraryGroup}>
        {libraryStrip('library-top')}
        <div style={styles.pile}>
          <Button data-testid="library-pile" onClick={() => setOpenZone(LIBRARY_ZONE)}>
            {ZONE_LABELS[LIBRARY_ZONE]}
          </Button>
          <span data-testid="library-pile-count">{state.zones.library.length}</span>
        </div>
        {libraryStrip('library-bottom')}
      </div>
      {renderPile('graveyard')}
      {renderPile('exile')}
      {renderPile('command')}

      {openZone !== null ? (
        <PileDialog
          collectionId={collectionId}
          session={session}
          zone={openZone}
          onClose={closeDialog}
        />
      ) : null}
    </section>
  );
};

PlaytestPiles.propTypes = {
  collectionId: PropTypes.string.isRequired,
  session: PropTypes.shape({
    engine: PropTypes.object.isRequired,
    cardViews: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  drops: PropTypes.shape({
    canDrop: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
  }).isRequired,
};

export default PlaytestPiles;
